import { useCanvases } from "@/api/canvas";
import {
  flowScheduleErrorMessage,
  useCreateFlowSchedule,
} from "@/api/flowSchedules";
import { useWorkspaces } from "@/api/workspaces";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { ROUTES } from "@/constants/routes";
import { useAppNavigate, useMainRouteParams } from "@/hooks/pane-routing";
import {
  DEFAULT_AUTOMATION_HARNESS,
  type AutomationHarnessSelection,
} from "@/panes/automation/automationHarness";
import {
  AutomationHarnessFields,
  useAutomationHarnessVisible,
} from "@/panes/automation/AutomationHarnessFields";
import { AutomationScheduleFields } from "@/panes/automation/AutomationScheduleFields";
import {
  defaultScheduleDraft,
  draftToCron,
  type ScheduleDraft,
} from "@/panes/automation/scheduleDraft";
import { cn } from "@/utils";
import { route } from "@/utils/route";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, ChevronDown, Frame, Layers } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import * as z from "zod";

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  instructions: z.string().min(1, "Instructions are required"),
  // Required; the picker starts empty (undefined) and zod's required_error
  // carries the fix-forward message.
  location: z.object(
    {
      kind: z.enum(["workspace", "canvas"]),
      id: z.string(),
      name: z.string(),
    },
    {
      required_error: "Pick where the automation runs",
      invalid_type_error: "Pick where the automation runs",
    },
  ),
});

type FormData = z.infer<typeof formSchema>;
type LocationValue = FormData["location"];

function LocationPicker({
  value,
  onChange,
}: {
  value: LocationValue | null;
  onChange: (location: LocationValue) => void;
}) {
  const [open, setOpen] = useState(false);
  const { data: workspaces = [] } = useWorkspaces();
  const { data: canvases = [] } = useCanvases();

  const Icon = value?.kind === "canvas" ? Frame : Layers;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-sm transition-colors hover:bg-n-05 focus-visible:outline-2 focus-visible:outline-rust focus-visible:outline-offset-[-2px]"
          aria-label="Location"
        >
          {value ? (
            <>
              <Icon className="size-3.5 shrink-0 text-n-50" />
              <span className="min-w-0 flex-1 truncate text-left">
                {value.name}
              </span>
            </>
          ) : (
            <span className="min-w-0 flex-1 truncate text-left text-muted-foreground/60">
              Pick a workspace or canvas
            </span>
          )}
          <ChevronDown className="size-3.5 shrink-0 text-n-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0">
        <Command>
          <CommandInput placeholder="Search workspaces and canvases" />
          <CommandList>
            <CommandEmpty>Nothing matches.</CommandEmpty>
            <CommandGroup heading="Workspaces">
              {workspaces.map((workspace) => (
                <CommandItem
                  key={workspace.id}
                  value={`workspace-${workspace.name}-${workspace.id}`}
                  onSelect={() => {
                    onChange({
                      kind: "workspace",
                      id: workspace.id,
                      name: workspace.name,
                    });
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "size-3.5",
                      value?.kind === "workspace" && value.id === workspace.id
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                  <Layers className="size-3.5 text-n-50" />
                  <span className="truncate">{workspace.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandGroup heading="Canvases">
              {canvases.map((canvas) => (
                <CommandItem
                  key={canvas.id}
                  value={`canvas-${canvas.name}-${canvas.id}`}
                  onSelect={() => {
                    onChange({
                      kind: "canvas",
                      id: canvas.id,
                      name: canvas.name,
                    });
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "size-3.5",
                      value?.kind === "canvas" && value.id === canvas.id
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                  <Frame className="size-3.5 text-n-50" />
                  <span className="truncate">{canvas.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Pane create mode (`/automations/new`). The schedule defaults to daily at
 * 9:00am in the browser's zone so a new automation is alive with a visible
 * next run; "No schedule" creates it paused.
 */
export function AutomationCreateForm() {
  const appNavigate = useAppNavigate();
  const params = useMainRouteParams();
  const { data: workspaces = [] } = useWorkspaces();
  const { data: canvases = [] } = useCanvases();
  const createSchedule = useCreateFlowSchedule();
  const [scheduleDraft, setScheduleDraft] = useState<ScheduleDraft>(() =>
    defaultScheduleDraft(),
  );
  const [harness, setHarness] = useState<AutomationHarnessSelection>(
    DEFAULT_AUTOMATION_HARNESS,
  );
  const harnessVisible = useAutomationHarnessVisible(harness);

  // Prefill the location from the surface the user is looking at.
  const contextCanvas = params.canvasId
    ? canvases.find((canvas) => canvas.id === params.canvasId)
    : undefined;
  const contextWorkspace = params.workspaceId
    ? workspaces.find((workspace) => workspace.id === params.workspaceId)
    : undefined;

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      instructions: "",
    },
  });

  // Seed the location once context data lands (canvas/workspace lists load
  // async); never clobber a user's explicit pick.
  const location = form.watch("location");
  useEffect(() => {
    if (location || form.formState.dirtyFields.location) return;
    if (contextCanvas) {
      form.setValue("location", {
        kind: "canvas",
        id: contextCanvas.id,
        name: contextCanvas.name,
      });
    } else if (contextWorkspace) {
      form.setValue("location", {
        kind: "workspace",
        id: contextWorkspace.id,
        name: contextWorkspace.name,
      });
    }
  }, [contextCanvas, contextWorkspace, form, location]);

  const handleSubmit = (data: FormData) => {
    const target = data.location;
    const cron = draftToCron(scheduleDraft);
    createSchedule.mutate(
      {
        name: data.name.trim(),
        instructions: data.instructions.trim(),
        ...(target.kind === "canvas"
          ? { canvas_id: target.id }
          : { workspace_id: target.id }),
        ...(cron ? { cron, cron_time_zone: scheduleDraft.timeZone } : {}),
        harness: harness.harness,
        model: harness.model,
        reasoning_effort: harness.effort,
      },
      {
        onSuccess: (created) => {
          appNavigate({
            pane: "rightPane",
            to: route(ROUTES.AUTOMATIONS.SHOW, { id: created.id }),
            // Replace so pane-back from the new automation skips the form.
            options: { replace: true },
          });
        },
        onError: (error) => {
          toast.error(
            flowScheduleErrorMessage(error, "Failed to create automation."),
          );
        },
      },
    );
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="space-y-5"
        noValidate
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Morning briefing" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="instructions"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Instructions</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Summarize what changed in this workspace and leave a note on the canvas."
                  className="min-h-[120px] resize-none"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                The agent gets this prompt at the start of every run.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="location"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Location</FormLabel>
              <FormControl>
                <LocationPicker
                  value={field.value ?? null}
                  onChange={(next) =>
                    form.setValue("location", next, {
                      shouldDirty: true,
                      shouldValidate: form.formState.isSubmitted,
                    })
                  }
                />
              </FormControl>
              <FormDescription>
                Runs use this workspace or canvas&apos;s files, connectors, and
                chat.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {harnessVisible && (
          <div className="space-y-2">
            <FormLabel>Runs on</FormLabel>
            <AutomationHarnessFields
              value={harness}
              onChange={setHarness}
              defaultsToPreferredModel
            />
            <p className="text-xs text-n-40">
              Every run this automation starts uses this engine.
            </p>
          </div>
        )}

        <div className="space-y-2">
          <FormLabel>Schedule</FormLabel>
          <AutomationScheduleFields
            value={scheduleDraft}
            onChange={setScheduleDraft}
          />
          <p className="text-xs text-n-40">
            {scheduleDraft.scheduleType === "none"
              ? "Created paused — set a schedule any time."
              : "Recurring runs must be at least 30 minutes apart."}
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button
            type="button"
            variant="outline"
            onClick={() => appNavigate({ pane: "rightPane", to: null })}
          >
            Cancel
          </Button>
          <Button type="submit" isLoading={createSchedule.isPending}>
            Create automation
          </Button>
        </div>
      </form>
    </Form>
  );
}
